<template>
  <button class="ehf-confetti-button" :class="[{ active, animating }, `direction-${direction}`]" @click="handleConfettiBtnClick">
    <div class="icon">
      <div class="cannon"></div>
      <div class="confetti">
        <div class="confetti-content">
          <svg viewBox="0 0 18 16">
            <polyline points="1 10 4 7 4 5 6 1" />
            <path d="M4,13 C5.33333333,9 7,7 9,7 C11,7 12.3340042,6 13.0020125,4" />
            <path d="M6,15 C7.83362334,13.6666667 9.83362334,12.6666667 12,12 C14.1663767,11.3333333 15.8330433,9.66666667 17,7" />
          </svg>
          <i></i>
          <i></i>
          <i></i>
          <i></i>
          <i></i>
          <i></i>
        </div>
        <div class="emitter"></div>
      </div>
    </div>
  </button>
</template>

<script>
  export default {
    name: 'EhfConfettiButton',
  }
</script>

<script setup>
  import { onMounted, toRefs, ref, nextTick } from 'vue'
  import gsap from 'gsap'
  import { loadFile } from '@/utils'
  import { debounce } from 'lodash'

  const props = defineProps({
    active: {
      type: Boolean,
      default() {
        return false
      },
    },
    direction: {
      type: String,
      default() {
        return 'left'
      },
    },
  })
  const emit = defineEmits(['stateChange', 'update:active'])

  const { active, direction } = toRefs(props)
  const animating = ref(false)
  const handleConfettiBtnClick = debounce(
    (e) => {
      const button = e.currentTarget
      if (!button) {
        return
      }
      const targetActiveState = !active.value
      emit('update:active', targetActiveState)
      emit('stateChange', targetActiveState)
      nextTick(() => {
        if (active.value) {
          animating.value = true
          gsap.to(button, {
            '--icon-x': -3,
            '--icon-y': 3,
            '--z-before': 0,
            duration: 0.2,
            onComplete() {
              let paramArr = [15, 6, -140, -110]
              if (direction.value == 'right') {
                paramArr = [-4, 6, -80, -50]
              }
              particles(button.querySelector('.emitter'), 80, ...paramArr)
              gsap.to(button, {
                '--icon-x': 0,
                '--icon-y': 0,
                '--z-before': -6,
                duration: 1,
                ease: 'elastic.out(1, .5)',
                onComplete() {
                  animating.value = false
                },
              })
            },
          })
        }
      })
    },
    500,
    { leading: true }
  )
  const particles = (parent, quantity, x, y, minAngle, maxAngle) => {
    let colors = ['#FFFF04', '#EA4C89', '#892AB8', '#4AF2FD']
    for (let i = quantity - 1; i >= 0; i--) {
      let angle = gsap.utils.random(minAngle, maxAngle),
        velocity = gsap.utils.random(70, 140),
        dot = document.createElement('div')
      dot.style.setProperty('--b', colors[Math.floor(gsap.utils.random(0, 4))])
      parent.appendChild(dot)
      gsap.set(dot, {
        opacity: 0,
        x: x,
        y: y,
        scale: gsap.utils.random(0.4, 0.7),
      })
      gsap
        .timeline({
          onComplete() {
            dot.remove()
          },
        })
        .to(
          dot,
          {
            duration: 0.05,
            opacity: 1,
          },
          0
        )
        .to(
          dot,
          {
            duration: 1.8,
            rotationX: `-=${gsap.utils.random(720, 1440)}`,
            rotationZ: `+=${gsap.utils.random(720, 1440)}`,
            physics2D: {
              angle: angle,
              velocity: velocity,
              gravity: 120,
            },
          },
          0
        )
        .to(
          dot,
          {
            duration: 1,
            opacity: 0,
          },
          0.8
        )
    }
  }
  onMounted(async () => {
    await loadFile(['/static/libs/Physics2DPlugin3.js'])
    gsap.registerPlugin(window.Physics2DPlugin)
  })
</script>

<style lang="scss">
  .ehf-confetti-button {
    --background: #1e2235;
    --color: #f6f8ff;
    --shadow: #{rgba(#00093d, 0.24)};
    --cannon-shadow: rgba(13, 15, 24, 0.2);
    --cannon-dark: #d1d6ee;
    --cannon-light: #ffffff;
    --confetti-1: #892ab8;
    --confetti-2: #ea4c89;
    --confetti-3: #ffff04;
    --confetti-4: #4af2fd;
    --z-before: -6;
    display: block;
    outline: none;
    cursor: pointer;
    position: relative;
    border: 0;
    background: none;
    font-family: inherit;
    font-weight: 600;
    font-size: 14px;
    color: var(--color);
    appearance: none;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
    transition: transform var(--transform-duration, 0.4s);
    will-change: transform;
    transform-style: preserve-3d;
    transform: perspective(440px) translateZ(0);
    &:hover {
      --transform-duration: 0.16s;
    }
    &.animating {
      --confetti-scale: 0;
      --stroke-dashoffset: 15;
    }
    &.direction-right {
      .icon {
        .cannon {
          transform: translate(0, 3px) rotate(-45deg);
        }
        .confetti {
          left: 17px;
          bottom: 9px;
          .confetti-content {
            transform: none;
          }
        }
      }
    }
    .icon,
    span {
      display: inline-block;
      vertical-align: top;
      position: relative;
      z-index: 1;
    }
    .icon {
      --z: 2px;
      width: 24px;
      height: 14px;
      transform: translate(calc(var(--icon-x, 0) * 1px), calc(var(--icon-y, 0) * 1px)) translateZ(2px);
      .confetti {
        position: absolute;
        left: -10px;
        bottom: 11px;
        .confetti-content {
          transform: rotate(-90deg);
        }
        svg {
          width: 18px;
          height: 16px;
          display: block;
          stroke-width: 1px;
          fill: none;
          stroke-linejoin: round;
          stroke-linecap: round;
          * {
            transition: stroke-dashoffset 0.2s;
            stroke-dasharray: 15 20;
            stroke-dashoffset: var(--stroke-dashoffset, 0);
            stroke: var(--stroke-all, var(--stroke, var(--confetti-2)));
            &:nth-child(2) {
              --stroke: var(--confetti-3);
            }
            &:nth-child(3) {
              --stroke: var(--confetti-1);
            }
          }
        }
        .emitter {
          position: absolute;
          left: 4px;
          bottom: 4px;
          pointer-events: none;
          div {
            width: 4px;
            height: 4px;
            margin: -2px 0 0 -2px;
            border-radius: 1px;
            position: absolute;
            left: 0;
            top: 0;
            transform-style: preserve-3d;
            background: var(--confetti-all, var(--b, none));
          }
        }
        i {
          width: 4px;
          height: 4px;
          display: block;
          transform: scale(var(--confetti-scale, 0.5));
          position: absolute;
          transition: transform 0.25s;
          left: var(--left, -1px);
          top: var(--top, 3px);
          border-radius: var(--border-radius, 1px);
          background: var(--confetti-background, var(--confetti-3));
          &:nth-child(2) {
            --left: 9px;
            --top: -1px;
            --border-radius: 2px;
            --confetti-background: var(--confetti-4);
          }
          &:nth-child(3) {
            --left: 5px;
            --top: 3px;
            --confetti-background: var(--confetti-1);
          }
          &:nth-child(4) {
            --left: 10px;
            --top: 14px;
            --confetti-background: var(--confetti-2);
          }
          &:nth-child(5) {
            --left: 9px;
            --top: 7px;
            --confetti-background: var(--confetti-4);
          }
          &:nth-child(6) {
            --left: 6px;
            --top: 8px;
            --border-radius: 2px;
            --confetti-background: var(--confetti-2);
          }
        }
      }
      .cannon {
        position: relative;
        width: 24px;
        height: 14px;
        transform: translate(0, 3px) rotate(-135deg);
        filter: drop-shadow(-1px 1px 1px var(--cannon-shadow));
        &:before,
        &:after {
          content: '';
          display: block;
          height: 14px;
        }
        &:before {
          background: linear-gradient(var(--cannon-dark), var(--cannon-light) 50%, var(--cannon-dark));
          width: 100%;
          -webkit-clip-path: polygon(25px -1px, 0 52%, 25px 15px);
          clip-path: polygon(25px -1px, 0 52%, 25px 15px);
        }
        &:after {
          width: 6px;
          position: absolute;
          right: -3px;
          top: 0;
          border-radius: 50%;
          box-shadow: inset 0 0 0 0.5px var(--cannon-light);
          background: linear-gradient(90deg, var(--cannon-dark), var(--cannon-light));
        }
      }
    }
    &.active {
      --background: #fff;
      --color: #1e2235;
      --border: #e1e6f9;
      --shadow: none;
      --cannon-dark: #103fc5;
      --cannon-light: #275efe;
      --cannon-shadow: #{rgba(#00093d, 0.2)};
      &:before {
        box-shadow: inset 0 0 0 1px var(--border);
      }
    }
  }
</style>
